/**
 * CetusEd Community Portal — Message Inbox
 *
 * COMPLIANCE: IDEA §300.501(c) — Parents must be afforded an opportunity to
 *   participate in meetings. The inbox provides a written record of
 *   communications with school staff, supporting due process hearings.
 *
 *   FERPA §1232g(a)(1) — Parents may access their child's education records.
 *   Messages sent through the portal become part of the student's education
 *   record and are subject to audit under FERPA.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { useInbox } from '../../api/hooks';
import { Card } from '../../ui/components/Card';
import { Button } from '../../ui/components/Button';
import { LoadingSpinner } from '../../ui/components/LoadingSpinner';

export const Inbox: React.FC = () => {
  const { data: threads, isLoading, error } = useInbox();

  return (
    <div className="inbox-page">
      <div className="page-header">
        <h1>Messages</h1>
        <Link to="/messages/compose">
          <Button>New Message</Button>
        </Link>
      </div>

      {isLoading && <LoadingSpinner />}

      {error && (
        <div className="error-message" role="alert">
          Unable to load messages. Please try again later.
        </div>
      )}

      {threads && threads.length === 0 && (
        <p className="empty-state">No messages yet. Staff messages will appear here.</p>
      )}

      {threads && threads.length > 0 && (
        <div className="thread-list">
          {threads.map((thread) => (
            <Link key={thread.id} to={`/messages/${thread.id}`} className="thread-link">
              <Card>
                <div className="thread-header">
                  <h3 className={thread.unread ? 'thread-unread' : ''}>
                    {thread.subject}
                    {thread.unread && <span className="unread-badge" aria-label="Unread" />}
                  </h3>
                  <span className="thread-date">
                    {new Date(thread.lastMessageAt).toLocaleDateString()}
                  </span>
                </div>
                <p className="thread-participants">
                  {thread.participants.map((p) => p.name).join(', ')}
                </p>
                <p className="thread-count">
                  {thread.messageCount} message{thread.messageCount !== 1 ? 's' : ''}
                </p>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};
